import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Building2, 
  Save, 
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  Globe,
  Map
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { fetchClubById, createClub, updateClub } from '../services/clubsApi';
import CustomSelect from '../components/common/CustomSelect';

const AdminClubEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    website: '',
    holes: '18',
    par: '',
    latitude: '',
    longitude: '',
    active: true
  });

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      loadClub();
    }
  }, [id]);

  const loadClub = async () => {
    try {
      setLoading(true);
      const club = await fetchClubById(parseInt(id));
      setFormData({
        name: club.name || '',
        address: club.address || '',
        city: club.city || '',
        postal_code: club.postal_code || '',
        phone: club.phone || '',
        email: club.email || '',
        website: club.website || '',
        holes: club.holes?.toString() || '18',
        par: club.par?.toString() || '',
        latitude: club.latitude?.toString() || '',
        longitude: club.longitude?.toString() || '',
        active: !!club.active
      });
    } catch (error) {
      console.error('Error loading club:', error);
      alert('Failed to load club');
      navigate('/admin/clubs');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Club name is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    if (formData.latitude && (isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90)) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }

    if (formData.longitude && (isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180)) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setSubmitting(true);

      const clubData = {
        ...formData,
        holes: formData.holes ? parseInt(formData.holes) : 18,
        par: formData.par ? parseInt(formData.par) : null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        active: formData.active ? 1 : 0
      };

      if (isEditMode) {
        await updateClub(parseInt(id), clubData);
        alert('Club updated successfully!');
      } else {
        await createClub(clubData);
        alert('Club created successfully!');
      }
      
      navigate('/admin/clubs');
    } catch (error) {
      console.error('Error saving club:', error);
      alert(error.message || `Failed to ${isEditMode ? 'update' : 'create'} club`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent mb-4 ${
            isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
          }`}></div>
          <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
            Loading club...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      {/* Header */}
      <div className={`sticky top-0 z-30 border-b ${
        isDarkMode
          ? 'bg-fsgu-gray-900/95 border-fsgu-gray-700 backdrop-blur-sm'
          : 'bg-fsgu-silver-50/95 border-fsgu-silver-200 backdrop-blur-sm'
      }`}>
        <div className="container-custom py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Left: Back + Title */}
            <div className="flex items-center gap-3">
              <Link
                to="/admin/clubs"
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'hover:bg-fsgu-gray-800 text-fsgu-silver-300'
                    : 'hover:bg-fsgu-silver-100 text-fsgu-silver-600'
                }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}>
                  {isEditMode ? 'Edit Club' : 'New Club'}
                </h1>
                <p className={`text-sm ${
                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                }`}>
                  {isEditMode ? 'Update club information' : 'Add a new golf club'}
                </p>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/admin/clubs')}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn btn-primary inline-flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEditMode ? 'Update' : 'Create'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container-custom py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Basic Information */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode 
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-lg font-semibold mb-6 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <Building2 className={`w-5 h-5 mr-2 ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
              }`} />
              Basic Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Club Name */}
              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Club Name <span className="text-fsgu-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`input ${errors.name ? 'border-red-500' : ''}`}
                  placeholder="e.g., Bloemfontein Golf Club"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Status
                </label>
                <CustomSelect
                  name="active"
                  value={formData.active ? '1' : '0'}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === '1' }))}
                  options={[
                    { value: '1', label: 'Active' },
                    { value: '0', label: 'Inactive' }
                  ]}
                  placeholder="Select status"
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode 
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-lg font-semibold mb-6 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <MapPin className={`w-5 h-5 mr-2 ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
              }`} />
              Location
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Address */}
              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Street Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="input"
                  placeholder="123 Golf Course Road"
                />
              </div>

              {/* City */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  City <span className="text-fsgu-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={`input ${errors.city ? 'border-red-500' : ''}`}
                  placeholder="Bloemfontein"
                />
                {errors.city && (
                  <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                )}
              </div>

              {/* Postal Code */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Postal Code
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  className="input"
                  placeholder="9301"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode 
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-lg font-semibold mb-6 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <Phone className={`w-5 h-5 mr-2 ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
              }`} />
              Contact Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Phone */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input"
                  placeholder="+27 51 123 4567"
                />
              </div>

              {/* Email */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`input ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="info@clubname.co.za"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Website */}
              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className={`input ${errors.website ? 'border-red-500' : ''}`}
                  placeholder="https://www.clubname.co.za"
                />
                {errors.website && (
                  <p className="text-red-500 text-sm mt-1">{errors.website}</p>
                )}
              </div>
            </div>
          </div>

          {/* Course Details */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode 
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-lg font-semibold mb-6 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <Building2 className={`w-5 h-5 mr-2 ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
              }`} />
              Course Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Holes */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Number of Holes
                </label>
                <input
                  type="number"
                  name="holes"
                  value={formData.holes}
                  onChange={handleChange}
                  className="input"
                  placeholder="18"
                />
              </div>

              {/* Par */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Course Par
                </label>
                <input
                  type="number"
                  name="par"
                  value={formData.par}
                  onChange={handleChange}
                  className="input"
                  placeholder="72"
                />
              </div>
            </div>
          </div>

          {/* GPS Coordinates */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode 
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-lg font-semibold mb-6 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <Map className={`w-5 h-5 mr-2 ${
                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
              }`} />
              GPS Coordinates
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Latitude */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Latitude
                </label>
                <input
                  type="text"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  className={`input ${errors.latitude ? 'border-red-500' : ''}`}
                  placeholder="-29.1211"
                />
                {errors.latitude && (
                  <p className="text-red-500 text-sm mt-1">{errors.latitude}</p>
                )}
              </div>

              {/* Longitude */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Longitude
                </label>
                <input
                  type="text"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  className={`input ${errors.longitude ? 'border-red-500' : ''}`}
                  placeholder="26.2153"
                />
                {errors.longitude && (
                  <p className="text-red-500 text-sm mt-1">{errors.longitude}</p>
                )}
              </div>
            </div>

            <p className={`text-sm mt-4 ${
              isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
            }`}>
              Get coordinates from{' '}
              <a 
                href="https://www.google.com/maps" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`font-medium hover:underline ${
                  isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-600'
                }`}
              >
                Google Maps
              </a>
              {' '}by right-clicking on the location
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminClubEditor;